import type { AfterViewInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, Input, signal, inject, viewChild } from '@angular/core';
import { CookieService } from 'ngx-cookie';
import {
  NoticeENComponent,
  NoticeDEComponent,
  NoticeHRComponent,
  NoticeITComponent,
  NoticeROComponent,
  NoticePLComponent,
} from './notices';
import { TranslateModule } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { NgIncludeDirective } from '@shared/layout/directives/ng-include';
import { DateTime } from 'luxon';

export const COOKIE_NAME = 'ic.cookie-accepted';

@Component({
  selector: 'ic-accept-cookies',
  templateUrl: './accept-cookies.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIncludeDirective, RouterLink, MatButton, TranslateModule],
})
export class AcceptCookiesComponent implements AfterViewInit {
  // injectables
  private readonly cookie = inject(CookieService);
  // inputs
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input()
  public set language(language: string) {
    this.includeNoticeByLanguageCode(language);
  }
  // viewChildren
  public readonly noticePlaceholder = viewChild(NgIncludeDirective);
  // state
  public readonly accepted = signal<boolean>(undefined);

  public constructor() {
    this.accepted.set(!!this.cookie.get(COOKIE_NAME));
  }

  public ngAfterViewInit() {
    this.includeNoticeByLanguageCode(this.language || 'en-GB');
  }

  public acceptCookiePolicy() {
    const expires = DateTime.now().plus({ month: 12 }).toJSDate();
    this.cookie.put(COOKIE_NAME, 'true', { expires });

    this.accepted.set(true);
  }

  private includeNoticeByLanguageCode(language: string) {
    const noticePlaceholder = this.noticePlaceholder();
    if (!noticePlaceholder) {
      return;
    }

    const viewContainer = noticePlaceholder.viewContainer;
    viewContainer.clear();

    const componentType = this.findTypeByLanguage(language);

    if (!componentType) {
      throw new Error(`No cookie notice found for selected language code: ${language}`);
    }

    viewContainer.createComponent(componentType);
  }

  private findTypeByLanguage(language: string) {
    switch (language) {
      case 'de': {
        return NoticeDEComponent;
      }
      case 'hr': {
        return NoticeHRComponent;
      }
      case 'it': {
        return NoticeITComponent;
      }
      case 'pl': {
        return NoticePLComponent;
      }
      case 'ro': {
        return NoticeROComponent;
      }
      case 'en':
      default: {
        return NoticeENComponent;
      }
    }
  }
}
